import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CreateOnboardCodeRequest,
  CreateResetCodeRequest,
  RedeemOnboardRequest,
  RedeemResetCodeRequest,
  RequestResetRequest,
  ResendVerificationRequest,
  ResetPasswordRequest,
  SetEmailRequest,
  UpdateUserRequest,
  VerifyEmailRequest,
  VerifyOwnEmailRequest,
} from '@wf/shared'
import { api } from '../api/client'

// Onboarding per Einmal-ID. Die öffentlichen Einlöse-Flows (kein Login) und die Chef-seitige Code-Erzeugung.

/** Öffentlich: Mitarbeiter löst Onboarding-Code ein → Auto-Login (status invalidieren, damit die App lädt). */
export function useRedeemOnboard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: RedeemOnboardRequest) => api.redeemOnboard(b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['status'] }),
  })
}

/** Öffentlich: Mitarbeiter setzt per Reset-Code ein neues Passwort (danach normaler Login). */
export function useRedeemResetCode() {
  return useMutation({ mutationFn: (b: RedeemResetCodeRequest) => api.redeemResetCode(b) })
}

/** Öffentlich: E-Mail-Reset anfordern (Antwort immer 200 — keine Existenz-Auskunft). */
export function useRequestReset() {
  return useMutation({ mutationFn: (b: RequestResetRequest) => api.requestReset(b) })
}

/** Öffentlich: E-Mail-Reset einlösen (Token aus dem gemailten Link) → neues Passwort. */
export function useResetPassword() {
  return useMutation({ mutationFn: (b: ResetPasswordRequest) => api.resetPassword(b) })
}

/** Öffentlich: Pflicht-E-Mail per Code verifizieren → eingeloggt (status neu laden). */
export function useVerifyEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: VerifyEmailRequest) => api.verifyEmail(b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['status'] }),
  })
}

/** Öffentlich: Bestätigungscode erneut anfordern (Antwort immer 200). */
export function useResendVerification() {
  return useMutation({ mutationFn: (b: ResendVerificationRequest) => api.resendVerification(b) })
}

/** Eingeloggt: eigene E-Mail hinzufügen/ändern (löst Code-Versand aus). */
export function useSetEmail() {
  return useMutation({ mutationFn: (b: SetEmailRequest) => api.setEmail(b) })
}

/** Eingeloggt: eigene E-Mail per Code verifizieren → status neu laden (emailVerified). */
export function useVerifyOwnEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: VerifyOwnEmailRequest) => api.verifyOwnEmail(b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['status'] }),
  })
}

/** Chef (cap team): Team-Liste — Basis für die Reset-Code-Vergabe je Mitarbeiter. */
export function useTeam() {
  return useQuery({ queryKey: ['users'], queryFn: api.listUsers })
}

/** Chef: Onboarding-Einmal-Code erzeugen (Klartext-Code genau einmal in der Antwort). */
export function useCreateOnboardCode() {
  return useMutation({ mutationFn: (b: CreateOnboardCodeRequest) => api.createOnboardCode(b) })
}

/** Chef: Reset-Einmal-Code für einen Mitarbeiter erzeugen. */
export function useCreateResetCode() {
  return useMutation({ mutationFn: (b: CreateResetCodeRequest) => api.createResetCode(b) })
}

/** Chef/Zweitchef: einen niedrigerrangigen Mitarbeiter bearbeiten (Position/Rechte). Server erzwingt Rang +
 *  re-filtert die Rechte. Erfolg → Team-Liste neu laden. */
export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateUserRequest }) => api.updateUser(id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  })
}
